// 新牌局入口：點選牌桌 → 選底台 → 擲骰。
// === 使用圖片 ===
// 背景與外框沿用 table-background / page-skin（詳見各元件檔頭）。
// CTileSprite 使用 tile-blank-front 與 tile-sheet（詳見 tile-sprite）。
// public/assets/ui/cream-mascot-peek.png（CREAM_MASCOT_PEEK_KEY）— 主角原圖，先平滑縮圖至專用 CanvasTexture。
// 選桌牌面、紅點、焦點框：純 Phaser.Graphics 繪製；右上 X 使用 close-button，詳見該檔頭。

import Phaser from 'phaser';
import { createCloseButton, CloseButtonLayout } from '@ui/close-button';
import { animateTo, reveal, glow, leaveScene, reducedMotion, Motion } from '@ui/motion';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '@config/game-config';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, syncBackgroundMusic } from '@audio/scene-audio';
import { TABLE_CATALOG, TableId, findTable, type TTableId } from '@core/table-catalog';
import { Suit } from '@core/types';
import { loadGameSave } from '@storage/game-save';
import { TABLE_ECONOMY } from '@net/account-rules';
import { isServerTableEnabled } from '@net/server-mode';
import { maybeShowHelpPrompt } from '@ui/help-prompt';
import { showNotice } from '@ui/notice-toast';
import { createTableBackground } from '@ui/table-background';
import { createPagePanel, createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { CTileSprite } from '@ui/tile-sprite';
import {
  CSpatialKeyboardNavigator,
  KeyboardNavigationResult,
  type IKeyboardFocusTarget,
} from '@input/spatial-keyboard-navigator';
import { SceneKey } from './scene-keys';
import { CREAM_MASCOT_PEEK_KEY, CREAM_ASSET_FRAME } from './boot-scene';

const CARD_ENTER_MS = 200; // 三張合計 280ms
const CARD_STAGGER_MS = 40; // 首次進场錯開
const CARD_ENTER_OFFSET = 12; // 卡片進場距離
const CARD_LIFT = 4; // 焦點抬升
const CARD_PRESS_SCALE = 0.98; // 入座壓縮
const CARD_FOCUS_MS = 120; // 焦點移動時間
const MASCOT_ENTER_MS = 280; // 探頭一次
const CENTER_X = LOGICAL_WIDTH / 2; // 水平中心
const CENTER_Y = LOGICAL_HEIGHT / 2; // 垂直中心
const PANEL = { width: 1110, height: 540 } as const;
const CARD = {
  width: 228, // 牌形按鈕寬度
  height: 290, // 牌形按鈕高度
  gap: 44, // 三桌間距
  y: 390, // 牌列中心
  radius: 20, // 短圓角，保留麻將牌輪廓
  baseOffset: 6, // 綠色牌底厚度
  tileY: -79, // 圖示相對牌面中心
  titleY: -6, // 名稱相對牌面中心
  dividerY: 26, // 分隔線相對中心
  dividerHalfWidth: 54, // 分隔線半寬
  descriptionY: 62, // 說明相對牌面中心
  enterY: 116, // 入座提示相對牌面中心
  pipInset: 22, // 紅點距角落
  pipRadius: 3, // 小紅點半徑
} as const;
const CARD_DETAIL = {
  inset: 8, // 內框距奶油外緣
  innerRadius: 13, // 與外框同心的細圓角
  shadowY: 10, // 桌布上的柔和落影
  shadowAlpha: 0.16, // 保留平面插畫感
  medallionRadius: 50, // 麻將圖示的圓形襯底
  medallionInset: 5, // 襯底雙線間距
  ornamentY: -79, // 左右紋飾與圖示對齊
  ornamentX: 74, // 紋飾橫向位置
  ornamentSize: 3, // 小菱形半徑
  dividerGap: 10, // 中央菱形周圍留白
  pipRingRadius: 6, // 紅點外圍暖金細圈
  footerWidth: 174, // 入座區域與兩側留白
  footerHeight: 34, // 入座區域高度
  footerRadius: 8, // 入座區域短圓角
  footerInset: 3, // 入座區域內側細線
} as const;
const CARD_INK = {
  inner: 0xfff4dc,
  innerHover: 0xfff8e9,
  border: 0xcbb78f,
  medallion: 0xf1e4c5,
  medallionLine: 0xdbc79d,
  footer: 0x285b45,
  footerHover: 0x36734f,
  footerLine: 0x719171,
  shadow: 0x072e22,
  action: '#fff2d3',
} as const;
const MASCOT = {
  x: CENTER_X - PANEL.width / 2 + 134, // 雙手位於圓角結束後的水平框線
  bottom: CENTER_Y - PANEL.height / 2 + 8, // 雙手下緣搭住上框，牌身露在框外
  width: 104, // 保持原比例，留出頂部安全空間
} as const;
const INK = {
  face: 0xffedc9,
  hover: 0xfff5df,
  base: 0x36734f,
  edge: 0xd8c6a1,
  coral: 0xeb5147,
  blush: 0xefb4a0,
  dark: '#174b37',
  muted: '#53705a',
  action: '#a33d32',
} as const;
const TITLE_Y = CENTER_Y - PANEL.height / 2 + 54; // 標題隨外框上緣對齊
const SUBTITLE_Y = TITLE_Y + 46; // 副標與標題固定間距
const CARD_TITLE_SIZE = 30; // 牌桌名稱字級
const DESCRIPTION_SIZE = 22; // 牌桌說明字級
const SMALL_SIZE = 20; // 副標與入座提示字級
const TILE_SCALE = 0.56; // 沿用遊戲麻將牌圖示
const EDGE_WIDTH = 1; // 奶油牌面細邊
const FOCUS_WIDTH = 2; // 鍵盤焦點框寬度
const FOCUS_GAP = 6; // 焦點框距牌面
const PRESS_OFFSET = 2; // 按下整張牌下移
const MASCOT_SAMPLE_SCALE = 2; // 先縮至顯示尺寸兩倍，避免大倍率直接縮放造成鋸齒
const MASCOT_TEXTURE_KEY = 'table-select-mascot-smoothed'; // 本場景專用的平滑縮圖快取

export interface ITableSelectData {
  readonly tableId?: TTableId;
}

export class CTableSelectScene extends Phaser.Scene {
  private selectedId: TTableId = TableId.Beginner;
  private leaving = false;
  private preview = false;
  private readonly navigator = new CSpatialKeyboardNavigator();

  constructor() {
    super({ key: SceneKey.TableSelect });
  }

  create(data: ITableSelectData = {}): void {
    this.leaving = false;
    this.cameras.main.resetFX();
    this.selectedId = findTable(data.tableId)?.id ?? TableId.Beginner;
    syncBackgroundMusic(this, BGMTrack.Lobby);
    createTableBackground(this);
    this.preview =
      new URLSearchParams(window.location.search).get('scene') === SceneKey.TableSelect;
    try {
      if (!this.preview && loadGameSave().activeSession) {
        this.scene.start(SceneKey.Home);
        return;
      }
    } catch {
      this.scene.start(SceneKey.Home);
      return;
    }
    createPagePanel(this, CENTER_X, CENTER_Y, PANEL.width, PANEL.height);
    const firstVisit = !this.registry.get('motion-table-visited');
    this.registry.set('motion-table-visited', true);
    this.renderMascot(firstVisit);
    createPageTitle(this, CENTER_X, TITLE_Y, '選擇牌桌');
    this.add
      .text(CENTER_X, SUBTITLE_Y, '選一桌，開始今天的牌局', {
        fontFamily: PAGE_THEME.font,
        fontSize: `${SMALL_SIZE}px`,
        color: PAGE_THEME.muted,
      })
      .setOrigin(0.5);

    const targets: IKeyboardFocusTarget[] = TABLE_CATALOG.map((table, index) => {
      const x = CENTER_X + (index - (TABLE_CATALOG.length - 1) / 2) * (CARD.width + CARD.gap);
      const root = this.add.container(x, CARD.y);
      const entrance = this.add.container(0, 0, [root]);
      if (firstVisit)
        reveal(this, entrance, index * CARD_STAGGER_MS, CARD_ENTER_MS, CARD_ENTER_OFFSET);
      const art = this.add.graphics();
      root.add(art);
      root.add(
        new CTileSprite(this, 0, CARD.tileY, {
          id: index,
          suit: Suit.Man,
          rank: index + 1,
        }).setScale(TILE_SCALE)
      );
      for (const [y, text, size, color, bold] of [
        [CARD.titleY, table.name, CARD_TITLE_SIZE, INK.dark, true],
        [CARD.descriptionY, table.description, DESCRIPTION_SIZE, INK.muted, false],
        [CARD.enterY, '入座 ›', SMALL_SIZE, CARD_INK.action, true],
      ] as const) {
        root.add(
          this.add
            .text(0, y, text, {
              fontFamily: PAGE_THEME.font,
              fontSize: `${size}px`,
              fontStyle: bold ? 'bold' : 'normal',
              align: 'center',
              color,
            })
            .setOrigin(0.5)
        );
      }
      return this.bindButton(
        root,
        CARD.width,
        CARD.height,
        index,
        () => {
          glow(this, root.x, CARD.y, CARD.width, CARD.height);
          this.selectedId = table.id;
          this.continueToSetup();
        },
        (focused, hovered) => {
          const left = -CARD.width / 2;
          const top = -CARD.height / 2;
          art.clear();
          art
            .fillStyle(CARD_INK.shadow, CARD_DETAIL.shadowAlpha * (focused || hovered ? 2 : 1))
            .fillRoundedRect(left, top + CARD_DETAIL.shadowY, CARD.width, CARD.height, CARD.radius);
          art
            .fillStyle(INK.base)
            .fillRoundedRect(left, top + CARD.baseOffset, CARD.width, CARD.height, CARD.radius);
          art.fillStyle(INK.face).fillRoundedRect(left, top, CARD.width, CARD.height, CARD.radius);
          art
            .lineStyle(EDGE_WIDTH, CARD_INK.border)
            .strokeRoundedRect(left, top, CARD.width, CARD.height, CARD.radius);
          art
            .fillStyle(hovered ? CARD_INK.innerHover : CARD_INK.inner)
            .fillRoundedRect(
              left + CARD_DETAIL.inset,
              top + CARD_DETAIL.inset,
              CARD.width - CARD_DETAIL.inset * 2,
              CARD.height - CARD_DETAIL.inset * 2,
              CARD_DETAIL.innerRadius
            );
          art
            .lineStyle(EDGE_WIDTH, CARD_INK.border)
            .strokeRoundedRect(
              left + CARD_DETAIL.inset,
              top + CARD_DETAIL.inset,
              CARD.width - CARD_DETAIL.inset * 2,
              CARD.height - CARD_DETAIL.inset * 2,
              CARD_DETAIL.innerRadius
            );

          // 圓章承托遊戲牌面，左右小菱形呼應頁面標題飾線。
          art.fillStyle(CARD_INK.medallion).fillCircle(0, CARD.tileY, CARD_DETAIL.medallionRadius);
          art
            .lineStyle(EDGE_WIDTH, CARD_INK.medallionLine)
            .strokeCircle(0, CARD.tileY, CARD_DETAIL.medallionRadius)
            .strokeCircle(0, CARD.tileY, CARD_DETAIL.medallionRadius - CARD_DETAIL.medallionInset);
          const diamond = (x: number, y: number): void => {
            art.fillStyle(CARD_INK.border);
            art.beginPath();
            art.moveTo(x, y - CARD_DETAIL.ornamentSize);
            art.lineTo(x + CARD_DETAIL.ornamentSize, y);
            art.lineTo(x, y + CARD_DETAIL.ornamentSize);
            art.lineTo(x - CARD_DETAIL.ornamentSize, y);
            art.closePath();
            art.fillPath();
          };
          for (const side of [-1, 1]) diamond(side * CARD_DETAIL.ornamentX, CARD_DETAIL.ornamentY);
          art.lineStyle(EDGE_WIDTH, CARD_INK.border);
          art.lineBetween(
            -CARD.dividerHalfWidth,
            CARD.dividerY,
            -CARD_DETAIL.dividerGap,
            CARD.dividerY
          );
          art.lineBetween(
            CARD_DETAIL.dividerGap,
            CARD.dividerY,
            CARD.dividerHalfWidth,
            CARD.dividerY
          );
          diamond(0, CARD.dividerY);
          for (const side of [-1, 1]) {
            const pipX = side * (CARD.width / 2 - CARD.pipInset);
            const pipY = top + CARD.pipInset;
            art
              .lineStyle(EDGE_WIDTH, CARD_INK.medallionLine)
              .strokeCircle(pipX, pipY, CARD_DETAIL.pipRingRadius);
            art.fillStyle(INK.coral).fillCircle(pipX, pipY, CARD.pipRadius);
          }

          // 整張卡片共用同一點擊區；入座飾帶只提供操作提示。
          const footerLeft = -CARD_DETAIL.footerWidth / 2;
          const footerTop = CARD.enterY - CARD_DETAIL.footerHeight / 2;
          art
            .fillStyle(hovered ? CARD_INK.footerHover : CARD_INK.footer)
            .fillRoundedRect(
              footerLeft,
              footerTop,
              CARD_DETAIL.footerWidth,
              CARD_DETAIL.footerHeight,
              CARD_DETAIL.footerRadius
            );
          art
            .lineStyle(EDGE_WIDTH, CARD_INK.footerLine)
            .strokeRoundedRect(
              footerLeft + CARD_DETAIL.footerInset,
              footerTop + CARD_DETAIL.footerInset,
              CARD_DETAIL.footerWidth - CARD_DETAIL.footerInset * 2,
              CARD_DETAIL.footerHeight - CARD_DETAIL.footerInset * 2,
              CARD_DETAIL.footerRadius - CARD_DETAIL.footerInset
            );
          if (focused) {
            art
              .lineStyle(FOCUS_WIDTH, INK.face)
              .strokeRoundedRect(
                left - FOCUS_GAP,
                top - FOCUS_GAP,
                CARD.width + FOCUS_GAP * 2,
                CARD.height + FOCUS_GAP * 2 + CARD.baseOffset,
                CARD.radius + FOCUS_GAP
              );
          }
        }
      );
    });
    targets.push(
      createCloseButton(
        this,
        CENTER_X + PANEL.width / 2 - CloseButtonLayout.insetX,
        CENTER_Y - PANEL.height / 2 + CloseButtonLayout.insetY,
        () => this.goBack()
      )
    );
    this.navigator.setTargets(
      targets,
      TABLE_CATALOG.findIndex((table) => table.id === this.selectedId)
    );
    const keyboard = this.input.keyboard;
    const handler = (event: KeyboardEvent): void => {
      if (this.leaving) return;
      if (event.key === 'Escape' || event.key === 'Backspace') {
        event.preventDefault();
        this.goBack();
        return;
      }
      if (this.leaving) return;
      if (this.navigator.handleKey(event.key) !== KeyboardNavigationResult.Ignored)
        event.preventDefault();
    };
    keyboard?.on('keydown', handler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      keyboard?.off('keydown', handler);
      this.navigator.clear();
    });
  }

  private renderMascot(firstVisit: boolean): void {
    const frame = this.textures.getFrame(CREAM_MASCOT_PEEK_KEY, CREAM_ASSET_FRAME);
    if (!this.textures.exists(MASCOT_TEXTURE_KEY)) {
      const width = MASCOT.width * MASCOT_SAMPLE_SCALE;
      const height = Math.round((width * frame.height) / frame.width);
      const texture = this.textures.createCanvas(MASCOT_TEXTURE_KEY, width, height)!;
      const context = texture.getContext();
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        this.textures.get(CREAM_MASCOT_PEEK_KEY).getSourceImage() as HTMLImageElement,
        frame.cutX,
        frame.cutY,
        frame.cutWidth,
        frame.cutHeight,
        0,
        0,
        width,
        height
      );
      texture.refresh();
      texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    }
    const mascot = this.add
      .image(MASCOT.x, MASCOT.bottom, MASCOT_TEXTURE_KEY)
      .setOrigin(0.5, 1)
      .setScale(1 / MASCOT_SAMPLE_SCALE);
    if (firstVisit) reveal(this, mascot, 0, MASCOT_ENTER_MS);
  }

  private bindButton(
    root: Phaser.GameObjects.Container,
    width: number,
    height: number,
    index: number,
    activate: () => void,
    draw: (focused: boolean, hovered: boolean) => void
  ): IKeyboardFocusTarget {
    let focused = false;
    let hovered = false;
    let pressed = false;
    const y = root.y;
    const hit = this.add.zone(root.x, y, width, height).setInteractive({ useHandCursor: true });
    const refresh = (): void => {
      if (!root.scene) return;
      const scale = pressed && !reducedMotion() ? CARD_PRESS_SCALE : 1;
      animateTo(
        this,
        root,
        {
          y:
            y +
            (reducedMotion() ? 0 : pressed ? PRESS_OFFSET : focused || hovered ? -CARD_LIFT : 0),
          scaleX: scale,
          scaleY: scale,
        },
        pressed ? 0 : CARD_FOCUS_MS,
        Motion.ease
      );
      draw(focused, hovered);
    };
    hit.on('pointerover', () => {
      hovered = true;
      refresh();
    });
    hit.on('pointerout', () => {
      hovered = false;
      pressed = false;
      refresh();
    });
    hit.on('pointerdown', () => {
      pressed = true;
      refresh();
    });
    hit.on('pointerupoutside', () => {
      pressed = false;
      refresh();
    });
    hit.on('pointerup', () => {
      const shouldActivate = pressed;
      pressed = false;
      refresh();
      if (!shouldActivate || this.leaving) return;
      this.navigator.focus(index);
      activate();
    });
    refresh();
    return {
      x: root.x,
      y,
      activate,
      setFocused: (value) => {
        focused = value;
        refresh();
      },
    };
  }

  private continueToSetup(): void {
    if (this.leaving) return;
    // 伺服器模式：金幣不到入座門檻就提示，不進底台頁（伺服器開將時也會再檢查一次）
    if (isServerTableEnabled() && !this.preview) {
      const threshold = TABLE_ECONOMY[this.selectedId].threshold;
      const coins = loadGameSave().ledger.balances[0];
      if (coins < threshold) {
        if (maybeShowHelpPrompt('金幣不夠進這一桌', () => this.scene.start(SceneKey.Shop))) return;
        showNotice(
          `入座需要 ${threshold.toLocaleString()} 金幣\n你目前有 ${coins.toLocaleString()} 金幣`
        );
        return;
      }
    }
    this.leaving = true;
    playUIClick(this);
    leaveScene(this, () =>
      this.scene.start(SceneKey.GameSetup, { tableId: this.selectedId, preview: this.preview })
    );
  }

  private goBack(): void {
    if (this.leaving) return;
    this.leaving = true;
    playUIClick(this);
    this.scene.start(SceneKey.Home);
  }
}
